import React from "react";
import { MobxComponent } from "../../../mobx/components";
import { inject, observer } from "mobx-react";
import { withTranslation, WithTranslation } from "react-i18next";
import { IconClose } from "./icons";
import { Checkbox } from "@lib/components";
import {
	MenuItem,
	QuickSettingsContainer,
	MenuColorHeaderWrapper,
	MenuColorHeaderTitleWrapper,
	MenuColorBodyWrapper,
	MenuColorLabel,
	MenuColorFooterWrapper,
	MenuActionContainer,
	MenuActionItem,
	ButtonV2,
} from "./layout.c";
import { RestaurantUtils } from "@lib/common";

interface ReceiptPrinterRow {
	_id: string;
	name?: string;
}

interface Props extends WithTranslation {
	show?: boolean;
	onHide?: () => void;
	onSave?: (selectedPrinterIds: string[]) => void | Promise<void>;
	title?: string;
	/** Menu item id (v2 `id` or legacy dish `_id`) — must match `receipt.eligible_items` entries */
	itemId?: string;
}

interface State {
	selectedPrinterIds: string[];
}

@inject("store")
@observer
class MenuReceiptPrinterAssignItemClass extends MobxComponent<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			selectedPrinterIds: [],
		};
	}

	syncFromStore() {
		const { store } = this.injected;
		const itemId = this.props.itemId;
		const r = store.restaurant;
		const printers = (r?.settings?.printers || []) as ReceiptPrinterRow[];
		if (!itemId) {
			this.setState({ selectedPrinterIds: [] });
			return;
		}
		this.setState({
			selectedPrinterIds: RestaurantUtils.menu.receiptPrinterIdsAssigningItem(printers, itemId),
		});
	}

	componentDidMount() {
		if (this.props.show) this.syncFromStore();
	}

	componentDidUpdate(prevProps: Props) {
		if (
			this.props.show &&
			(!prevProps.show ||
				prevProps.itemId !== this.props.itemId)
		) {
			this.syncFromStore();
		}
	}

	togglePrinter(printerId: string) {
		this.setState((s) => ({
			selectedPrinterIds: s.selectedPrinterIds.includes(printerId)
				? s.selectedPrinterIds.filter((id) => id !== printerId)
				: [...s.selectedPrinterIds, printerId],
		}));
	}

	render() {
		const { show, onHide, onSave, title } = this.props;
		const { selectedPrinterIds } = this.state;
		const r = this.injected.store.restaurant;
		const printers = (r?.settings?.printers || []) as ReceiptPrinterRow[];

		return (
			<QuickSettingsContainer className={show ? "active" : ""}>
				<MenuColorHeaderWrapper>
					<MenuColorHeaderTitleWrapper>
						{title || "Receipt printers"}
					</MenuColorHeaderTitleWrapper>
					<MenuItem onClick={onHide}>
						<IconClose />
					</MenuItem>
				</MenuColorHeaderWrapper>
				<MenuColorBodyWrapper style={{ minHeight: "50vh", overflowY: "scroll" }}>
					<MenuColorLabel>
						Select which receipt printers should print this item (kitchen / receipt eligible items).
					</MenuColorLabel>
					<MenuActionContainer>
						{printers.length === 0 ? (
							<p style={{ padding: 16, color: "#6B7270" }}>
								No printers configured. Add printers in Settings → Printers.
							</p>
						) : (
							printers
								.filter((p): p is ReceiptPrinterRow & { _id: string } => !!p._id)
								.map((printer) => {
									const checked = selectedPrinterIds.includes(printer._id);
									return (
										<MenuActionItem
											key={printer._id}
											onClick={() => this.togglePrinter(printer._id)}
											className="menu-printer-item"
										>
											<div className="printer-checkbox">
												<Checkbox
													id={`receipt-printer-${printer._id}`}
													checked={checked}
													onChange={() => this.togglePrinter(printer._id)}
													onClick={(e) => e.stopPropagation()}
												/>
												{printer.name || printer._id}
											</div>
										</MenuActionItem>
									);
								})
						)}
					</MenuActionContainer>
				</MenuColorBodyWrapper>
				<MenuColorFooterWrapper>
					<ButtonV2 variant="default" onClick={onHide}>
						Cancel
					</ButtonV2>
					<ButtonV2 onClick={() => onSave?.(selectedPrinterIds)}>Save</ButtonV2>
				</MenuColorFooterWrapper>
			</QuickSettingsContainer>
		);
	}
}

export const MenuReceiptPrinterAssignItem = withTranslation()(MenuReceiptPrinterAssignItemClass);
